import { React, createContext, useCallback, useContext, useEffect, useReducer, useRef} from "react"
import { v4 as uuidv4 } from 'uuid';
import SockJS from "sockjs-client";
import useSmartRef from "../hooks/useSmartRef";
import { useOperatorContext } from "../comps/login/OperatorContextProvider";
import { webSocketsServerUrl } from "./EnvContextProvider";
import { useTokensContext } from "./TokensContextProvider";

// TODO: Implement an Inactive Queue and Global Silent handler for all subscriptions of unmounted pages, and add timeout to actually unsubscribe them on the server in one "batch".
//       Details:  https://chatgpt.com/c/67751c02-a998-8004-a0f8-dde17df0eca3


export const WebSocketContext = createContext();

 //---------------------------------  Static  -----------------------------------------//
//                                                                                    //

export const ConnStatus = {
  ON:       {title: 'On',       icon: '🟢'},
  OFF:      {title: 'Off',      icon: '🟤'},
  ERROR:    {title: 'Error',    icon: '🔴'},
  TIMEOUT:  {title: 'Timeout',  icon: '🏁'}
}

export const WebSocketFlag = {
  Connect:     'WebSocket-Connect',
  Connected:   'WebSocket-Connected',
  Disconnect:  'WebSocket-Disconnect',
  Disconnected:'WebSocket-Disconnected',
  Ready:       'WebSocket-Ready',
  Error:       'WebSocket-Error',
}

let connStatus = ConnStatus.OFF;
let connProgress = null;

const isConnected = () => connStatus === ConnStatus.ON;
const setDisconnected = () => connStatus = ConnStatus.OFF;
const setConnected = () => connStatus = ConnStatus.ON;
const setConnError = () => connStatus = ConnStatus.ERROR;



 //------------------[   WebSocketContextProvider   ]-----------------------------------//
//                                                                                     //

export default function WebSocketContextProvider({children}) {
  const {idToken, accessToken} = useTokensContext();
  const {operatorState, OperatorFlag, isOperatorFlag}  = useOperatorContext();  
  const stompClientRef = useRef();

  const getStompClient = useCallback(() => {
    return stompClientRef.current;
  }, []);

  // const connStatusRef = useRef(ConnStatus.OFF);
  // const connProgressRef = useRef(null);  
  
  // SUBS QUEUE Map
  // -------------------------
  // <topicName> : {
  //                  id: <the id>,
  //                  callback: <the handler function>
  //               }
  const {get:getSubsQueue} = useSmartRef({});      // path -> method   { topicPath: {id: "uuid-12ab", callback: "onMessage"} }
  
  // SUBS ACTIVE Map
  // --------------------------
  // <the id> : { 
  //              id: '<the Id>',  
  //              topicName: '<the topic name>' 
  //            }
  const {get:getSubsActive} = useSmartRef({});


  const webSocketReducer = (state, action) => {
    if (state.flag === action.type) {
      // console.debug('[WebSocketContextProvider REDUCER] Ignoring same state');
      return state;
    }
    const next = {...state, flag: action.type};
    // console.debug('[WebSocketContextProvider REDUCER] %s ---> %s', state.flag, next.flag);
    return next;
  }

  const [state, dispatch] = useReducer(webSocketReducer, { flag: WebSocketFlag.Disconnected });

  // ---[  dispatchWebSocketFlag  ]---
  const dispatchWebSocketFlag = (webSocketFlag) => {
    dispatch({type: webSocketFlag});
  }

  const isWebSocketFlag = useCallback((webSocketFlag) => {
    return state.flag === webSocketFlag;
  }, [state.flag]);

  const isWebSocketReady = useCallback(() => isWebSocketFlag(WebSocketFlag.Ready), [isWebSocketFlag]);

  // ---[  hasActiveId  ]---
  //
  const hasActiveId = useCallback((activeId) => {
    return getSubsActive()[activeId]?.topicName;
  }, [getSubsActive]);

  //
  // ---[  ref Is Connected  ]---
  //
  // const refIsConnected = useCallback(() => {
  //   return Boolean(connStatusRef.current) && Boolean(connStatusRef.current === ConnStatus.ON);
  // }, []);

  // const refSetDisconnected = useCallback(() => {
  //   connStatusRef.current = ConnStatus.OFF;
  // }, []);

  //
  // ---[  Stomp SUBSCRIBE  ]---
  //
  const stompSubscribe = useCallback((subsId, channelName, onMsg) => {
    // SUBSCRIBE
    const subRes = stompClientRef.current.subscribe(channelName, onMsg, {'id': subsId});

    // subId ==> ActiveData
    getSubsActive()[subRes.id] = {
      id: subRes.id,
      topicName: channelName
    };
    
    // no effect if not found
    delete getSubsQueue()[channelName];
    
  }, [getSubsActive, getSubsQueue]);

  //---[  Get Sub Is  ]---
  const getSubsId = useCallback((channelName) => {    
    // Look in the pending queue...
    const qChannel = getSubsQueue()[channelName];
    const isChannelPending = Boolean(qChannel?.id);
    
    // If found in pending, return its id.
    if (isChannelPending) {
      return qChannel.id;
    }

    // Not found in pending, 
    // So Look in the Active subs...
    const active = Object.values(getSubsActive()).find(v => v.topicName === channelName);

    // If found in actives, return its id, 
    // if not, return null.
    return active ? active.id : null;
  }, [getSubsActive, getSubsQueue]);


  //
  //---[  Subscribe  ]---
  //
  // Subscribe If Absent - OR - Return the subsId if already subscribed.
  // 
  const wsSubscribe = useCallback((topic, onMessage) => {
    const existingSubId = getSubsId(topic);
    if (existingSubId) {
      return existingSubId;
    }
    
    const subId = uuidv4();

    if (isConnected()) {
      stompSubscribe(subId, topic, onMessage);
      dispatchWebSocketFlag(WebSocketFlag.Ready);
    } 
    else {
      const subData = {
        id: subId,
        topicName: topic,
        onMsg: onMessage
      }
      getSubsQueue()[topic] = subData;        
    }

    // TODO: used later to unsubscribe so do we need more data here?
    return subId; 
  }, [getSubsId, getSubsQueue, stompSubscribe]); // TODO: Why the compiler doesn't complain about missing disp


  //
  //------[  wsUnsubscribe  ]------
  //
  const wsUnsubscribe = useCallback((subId) => {
    if (!subId) {
      console.error('[WebSocketContextProvider wsUnsubscribe] Empty subId - wsUnsubscribe Ignored.');
      return subId;
    }
   
    let topicName = null;
    let isDeletedFromActive = false;

    const isActive = hasActiveId(subId);

    if (isConnected()) {
      stompClientRef.current.unsubscribe(subId, {});
      if (!isActive) {
        console.error('[WebSocketContextProvider wsUnsubscribe] Connected but no Active subId: %s', subId);
      }
    }

    // get the channelName and delete the Active entry.
    if (isActive) {
      // if (!isConnected()) {
      //   console.warn('[WebSocketContextProvider wsUnsubscribe] Disconnected but still in Active List [subId: %s]', subId);
      // }
      topicName = getSubsActive()[subId].topicName;
      delete getSubsActive()[subId];
      isDeletedFromActive = true;
    }
    
    // If channel not found (not active), Search the Queue...
    let data;
    let isFoundInQueue = false;
    if (topicName === null) {
      const entry = Object.entries(getSubsQueue()).find(e => e[1].id === subId);
      topicName = entry?.[0];
      data = JSON.stringify(entry?.[1]);
      isFoundInQueue = Boolean(topicName) && Boolean(data);
    }
    
    if (isDeletedFromActive && isFoundInQueue) {
      console.error('[WebSocketContextProvider wsUnsubscribe] [topicName: %s] [subId: %s] BUG: subId Both in Active and Queue.', topicName, subId);
    }

    if (!isDeletedFromActive && !isFoundInQueue) {
      console.warn('[WebSocketContextProvider wsUnsubscribe] [topicName: %s] [subId: %s] Stale SubId? subId Not in Active nor Queue!', topicName, subId);
    }

    delete getSubsQueue()[topicName];
    if (isFoundInQueue) {
      // console.log('[WebSocketContextProvider wsUnsubscribe] [channelName: %s] Removed from the Queue.', topicName);
    }    
  }, [hasActiveId, getSubsActive, getSubsQueue]);

  
  //--- disconnect ---
  const disconnect = useCallback(() => {
    //--- onDisconnect ---
    const onDisconnect = () => {
      setDisconnected();
      dispatchWebSocketFlag(WebSocketFlag.Disconnected);
    }
    // TODO: Purge the Queue as well? (delete whatever is in the queue);
    Object.keys(getSubsActive()).forEach(subnId => {
      wsUnsubscribe(subnId);
    });     
    if (stompClientRef?.current && isConnected()) {  
      if (stompClientRef.current) {
        stompClientRef.current.disconnect(onDisconnect, {});
      }
    }
  }, [getSubsActive, wsUnsubscribe]);

  //
  // ---[  wsDisconnect  ]---
  //
  const wsDisconnect = useCallback(() => {
    disconnect();
  }, [disconnect]);


  //
  //
  // ------[   setupAndConnect   ]------
  //
  //
  const setupAndConnect = useCallback(() => {
    // console.debug('[WebSocketContextProvider setupAndConnect] [refIsConnected: %s] [webSocketFlag: %s]:', isConnected(), state.flag);
    if (isConnected() || !isWebSocketFlag(WebSocketFlag.Disconnected)) {
      // console.debug('Connector: Ignored');
      return;
    }

    if (!idToken) {
      // console.debug('Connector: Ignored - No idToken.');
      return;
    }

    if (!accessToken) {
      // console.debug('Connector: Ignored - No accessToken.');
      return;
    }    


    //
    // Assumption - The "Lucky First One" Grabbing the async assignment is the only one:
    //              1 -  With "Diverted Flow" into the async function.
    //              2 -  Able to return a new Promise, and then resolve or reject it, directly affecting the async result for others.

    // Assumption - The Thread Safe gatekeeper is the line of the actual assignment using `async` which I think it means:
    //              1.  Inside the async'ed function, it doesn't matter when the new Promise instance is created.
    //              2.  Others that reach the same async assignment will not go into the async'ed function and instead will be BLOCKED indefinitely until a Promise reference is returned.
    
    // Assumption - While the first one returning a Promise and performing its Only Once task, A decision needs to be done on how should the others continue or what should they do:
    //              E.g: 
    //              1. They get "Empty" result, and move on to their "empty data" flow.
    //              2. They Block until data is available ? -   
    //              3. Except Errors, any other options?
     
    // Lesson #1 - The `const guard = async ...` is the Thread Safe locking / gatekeeper.
    // Lesson #2 - The first one getting the "async" result, is the one that will DO THE ONLY ONCE JOB! (it is the one that has the opportunity to WRITE on the async result "Sorry, Taken" However, That is UNTIL THE Promise is RESOLVED.
    // Lesson #3 - The async guard is CANCELED when the Promise is RESOLVED.
    // Lesson #4 - The async guard is CANCELED when the Promise is REJECTED.
    // Lesson #5 - The async guard is CANCELED when the Promise is in ERROR.
    // Lesson #6 - To guarantee a job would occur "Only Once", the "AlreadyResolved" useRef must be set before the actual resolve so others will get an already initialized/assigned ref.current so no race conditions are possible while the ref is modified before resolve occur.
    // QUESTION #1 - if the Promise never created, what happens to the async guard?  seems to hang all others, but not necessarily hanging the first one which supposed to provide it.
    // QUESTION #2 - Are there any errors inside the async block that can not be detected by the async assignment?, which I think it means that could not be automatically "rejected"?  If yes, how to recover the guard or how to safely abort..
    // ASSUMPTION - explicit aborts should and can be made by REJECT the Promise.

    // Lesson #7 - When we Resolve, then duplication of the "Only Once" task becomes either allowed, unless the first one had the opportunity to establish a LOCK unaffected by rerenders - e.g: useRef.
    //             This means we "loose" the async guard, hence, we must already have by then a useRef to be the new gatekeeper.
    // QUESTION #3 - Since my goal is to run a task only once, at least until explicitly allowed again, then resolving the Promise, looses the native thread safety guard to prevent other attempts to run the same task.
    //               However, assigning the Promise resolved result into a useRef, should provide the "conversion" required having the useRef become the new gatekeeper, initialized before the resolve is made.
    //               And, it seems enough to only check if the useRef is not null to know if the task has already done, but I wonder if the resolved value itself can help even better with other possible flows I did not think about.
    //               For example, 1. future Retry logic on specific errors? 2. When exactly it would be safe to reset the useRef back to null? which means allowing new attempts to run the OnlyOnce task again... 
    //               This means, that The useRef value "lifecycle" is first null, then a Promise instance, and then I intend to place a resolved value in it or a rejected error code.
    //               I know I already doing that with the help of other useRefs and states, but it is "tempting" to consider using the Promised value and the useRef gatekeeper as a "consolidated" single point of access which sounds safer for my goal and if so, how to correctly use implement it?
    //     
    // So, could it be something like:     
    //        `const resolvedResultRef.current = doOnlyOnceTask();`
    //     And then everyone that gets here immediately after that line is basically all the others that "concurrently" attempted to do the same task.
    //        if (resolvedResultRef.current === 'AlreadyResolved') - Then: skip / block / warn or anything we explicity decide here.
    //        if (... Error ) - skip 

    const createStomClientPromise = async () => {

      dispatchWebSocketFlag(WebSocketFlag.Connect);
      
      const clientPromise = new Promise( (resolve, reject) => {
        
        const logAllActives = () => {
          Object.keys(getSubsActive()).forEach(subKey => console.log('[WebSocketContextProvider onConnect] ACTIVE - %s --> %s', subKey, JSON.stringify(getSubsActive()[subKey])));
        }

        //      TODO: REMOVED TO ENABLE DEBUG !!!  +   Bring back the clearTimeout below in both onConnect AND onError
        //      ---------------------------------
        // 
        const connTimeout = setTimeout(()=>{          
          // TODO: 1. Test that Disconnect occurs gracefully. 
          //       2. Set another timeout to Retry or simply eject the operator?
          disconnect();
          setConnError();
          //        <<<  REJECT  by Timeout  >>>
          reject("WebSocket Connection Timeout");
        }, 5000);
    
        //--- onConnect ---
        const onConnect = () => {
          clearTimeout(connTimeout);
          setConnected();
          dispatchWebSocketFlag(WebSocketFlag.Connected);
          const qCopy = {...getSubsQueue()};
          Object.keys(qCopy).forEach(topic => {
            const subReqData = qCopy[topic];
            stompSubscribe(subReqData.id, topic, subReqData.onMsg);
          });        

          logAllActives();
          stompClientRef.current.ws.onclose = onError;
          
          //        (((  RESOLVE  )))
          resolve(stompClientRef.current);          
          dispatchWebSocketFlag(WebSocketFlag.Ready);
        }

        //--- onError, we can not recover (auto reconnect) as we don't have the httpOnly cookie, so must re-login.
        const onError = (event) => {
          clearTimeout(connTimeout);
          setConnError();
          dispatchWebSocketFlag(WebSocketFlag.Error);
          console.error('WebSocket Connection ERROR:'+JSON.stringify(event));          
          disconnect(); // TODO: Reconnect Here...

          //        <<<  REJECT  by Error  >>>
          reject(new Error(JSON.stringify(event)));
        }

        //--- CONNECT ---        
        console.debug('[WebSocketContextProvider] CONNECTING NOW...');
        const webstomp = require('webstomp-client');
        const headersList = {
          "Authorization": accessToken,
          "auth-id-token": idToken,
          "client-id":"kiponos-web", // TODO: Test this header on the server side
        };

        stompClientRef.current = webstomp.over(new SockJS(webSocketsServerUrl, null, {transports: ['websocket']}));
        stompClientRef.current.outgoing = 13000;
        stompClientRef.current.incoming = 12000;
        stompClientRef.current.protocols = ['v12.stomp']; //'VERSIONS.V1_2';
        stompClientRef.current.connect(headersList, onConnect, onError);

      }).then( (resolvedValue) => {
        console.log('createStomClientPromise Resolved: %s', resolvedValue);
      }, (rejectedValue) => {
        console.error('createStomClientPromise REJECTED: %s', rejectedValue);        
      })
      .catch( (reason) => {
        console.error('createStomClientPromise CATCH Unexpected Error: %s', reason);
      }).finally(()=>{
        console.log('createStomClientPromise finally. Clear connProgressRef');
        // Served its role as lock sync so release it, from here the gate keeper is the websocket flag (that must be the "Connect" command)
        connProgress = null;
      });

      return clientPromise;
    }
  
    // --- PROMISE ---
    connProgress = createStomClientPromise();

  }, [accessToken, disconnect, idToken, stompSubscribe, getSubsActive, getSubsQueue, isWebSocketFlag]);

  // CONNECT On Operator Ready
  useEffect(()=>{
    if (isOperatorFlag(OperatorFlag.Ready) && !connProgress && !isConnected() && isWebSocketFlag(WebSocketFlag.Disconnected)) {
      setupAndConnect();
    }
  }, [operatorState, isOperatorFlag, setupAndConnect, OperatorFlag, isWebSocketFlag]);


  // ---[  isSubscribed  ]---
  //
  const isSubscribed = useCallback((channelName) => {
    const qChannel = getSubsQueue()[channelName];
    const isChannelPending = Boolean(qChannel?.id);
    const isChannelActive = Object.values(getSubsActive()).some(v => v.topicName === channelName);
    return isChannelActive || isChannelPending;
  }, [getSubsQueue, getSubsActive]);


  // The WebSocket Essentials
  const context = {
    getSubsId,
    getSubsQueue,
    isSubscribed,
    isWebSocketFlag,
    getStompClient,
    isConnected,
    stompSubscribe,    
    uuidv4,
    wsSubscribe,
    wsUnsubscribe,
    wsDisconnect,
    webSocketState:state,
    isWebSocketReady,
    dispatchWebSocketFlag,
    WebSocketFlag
  }
  
  return (
    <WebSocketContext.Provider value={context}>
      {children}
    </WebSocketContext.Provider>
  )  
}

// Hook serving the context
export function useWebSocketContext() {
  return useContext(WebSocketContext);
}
